import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Returns true when the given input is a phone number we're willing
 * to count as a real lead — i.e. fire the Google Ads `lead`
 * conversion for AND save into our potential-customers table.
 *
 * The old `isPhoneComplete` gate was just "≥ 9 digits", which let
 * through obvious garbage like 999999999, 123456789, 555555555 etc.
 * Bots and bored visitors fill those in and burn paid-click budget.
 *
 * Two-layer check:
 *
 * 1) libphonenumber-js validation. Tries international format first
 *    (anything starting with `+`), then falls back to Israeli national
 *    parsing for plain digit strings (since IL is the primary market).
 *    `isValid` checks country-specific length AND prefix rules — e.g.
 *    Israeli mobiles must start with 05[0-9] after the country code.
 *
 * 2) Pattern heuristic. Some numbers libphonenumber considers "valid"
 *    by structure are still obvious test/spam inputs: every digit the
 *    same (000000000, 999999999) or a long sequential run (123456789,
 *    987654321). We reject those explicitly.
 */
export function isValidLeadPhone(raw, defaultCountry = 'IL') {
  if (!raw || typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  if (!trimmed) return false;

  // libphonenumber check — try international then national.
  let parsed = null;
  if (trimmed.startsWith('+')) {
    parsed = parsePhoneNumberFromString(trimmed);
  } else {
    parsed = parsePhoneNumberFromString(trimmed, defaultCountry);
  }
  if (!parsed || !parsed.isValid()) return false;
  // Also reject the bare "isPossible but not isValid" case where the
  // structure-check parser returned something useless.
  if (!isValidPhoneNumber(parsed.number)) return false;

  // Pattern heuristic over the national digits — strip country code
  // so a +15555555555 number still gets caught by the all-same rule.
  const nationalDigits = String(parsed.nationalNumber || '').replace(/\D/g, '');
  if (nationalDigits.length < 7) return false;
  if (/^(\d)\1+$/.test(nationalDigits)) return false; // 0000000, 5555555, …
  if (isLongSequential(nationalDigits)) return false; // 123456789, 987654321 etc.

  return true;
}

/**
 * Returns true when the digit string contains an ascending or
 * descending run of 6+ consecutive numerals (e.g. "123456" or
 * "987654"). 6 is chosen as the cutoff because shorter sequential
 * substrings appear naturally in real phone numbers — Israeli phone
 * patterns like 050-1234567 are real even though 1234 is sequential.
 */
function isLongSequential(digits) {
  const run = 6;
  let asc = 1;
  let desc = 1;
  for (let i = 1; i < digits.length; i++) {
    const diff = Number(digits[i]) - Number(digits[i - 1]);
    asc = diff === 1 ? asc + 1 : 1;
    desc = diff === -1 ? desc + 1 : 1;
    if (asc >= run || desc >= run) return true;
  }
  return false;
}
